import { Colors } from '@vetahealth/fishing-gear/colors'
import { ColorSchemeName, Platform } from 'react-native'
import { DefaultTheme } from 'styled-components/native'

export function getTheme({
  colorScheme = 'light',
  width,
  height,
}: {
  colorScheme?: ColorSchemeName
  width: number
  height: number
}): DefaultTheme {
  const isLight = colorScheme !== 'dark'

  const lightColors = {
    primary: Colors.mauve800,
    secondary: Colors.mauve200,
    background: Colors.mauve50,
    secondaryBackground: Colors.mauve75,
    secondaryBackgroundHover: Colors.gray150,
    tertiaryBackground: Colors.mauve100,
    tertiaryBackgroundHover: Colors.mauve200,
    text: Colors.black,
    secondaryText: Colors.gray800,
    invertedText: Colors.white,
    disabled: Colors.mauve300,
    error: Colors.ruby600,
    errorBackground: Colors.ruby100,
    hint: Colors.gray700,
    border: Colors.mauve100,
    closeToast: Colors.mauve100,
    toggle: Colors.mauve800,
    toggleSelected: Colors.white,
    toggleText: Colors.mauve900,
    toastBackground: Colors.mauve900,
    toastText: Colors.mauve100,
    dialogBackground: Colors.mauve50,
    tabBarBackground: Colors.white,
    tabBarHighlight: Colors.mauve150,
    tabBarAddButton: Colors.mauve800,
    tabBarAddIcon: Colors.mauve50,
    tabBarIcon: Colors.mauve800,
    chartGridLines: Colors.gray300,
    trackingEventBackground: Colors.mauve75,
    deviceBackground: Colors.plum100,
    deviceText: Colors.mauve900,
    insightBackground: Colors.mauve150,
    insightText: Colors.mauve700,
    boxShadow: Colors.anthrazit500,
    patientMessage: Colors.mauve75,
    careManagerMessage: Colors.mauve100,
    primaryButtonBackground: Colors.mauve300,
    primaryButtonText: Colors.mauve900,
    primaryButtonHover: Colors.mauve400,
    secondaryButtonBackground: Colors.mauve100,
    secondaryButtonText: Colors.mauve700,
    secondaryButtonHover: Colors.mauve200,
  }

  const darkColors = {
    primary: Colors.mauve600,
    secondary: Colors.anthrazit500,
    background: Colors.black,
    secondaryBackground: Colors.anthrazit600,
    secondaryBackgroundHover: Colors.gray900,
    tertiaryBackground: Colors.anthrazit550,
    tertiaryBackgroundHover: Colors.gray1000,
    text: Colors.white,
    secondaryText: Colors.gray200,
    invertedText: Colors.black,
    disabled: Colors.gray800,
    error: Colors.ruby400,
    errorBackground: Colors.ruby900,
    hint: Colors.gray400,
    border: Colors.gray800,
    closeToast: Colors.mauve100,
    toggle: Colors.gray600,
    toggleSelected: Colors.mauve900,
    toggleText: Colors.white,
    toastBackground: Colors.mauve300,
    toastText: Colors.mauve900,
    dialogBackground: Colors.anthrazit700,
    tabBarBackground: Colors.anthrazit500,
    tabBarIcon: Colors.mauve300,
    tabBarHighlight: Colors.mauve900,
    tabBarAddButton: Colors.mauve400,
    tabBarAddIcon: Colors.mauve900,
    chartGridLines: Colors.gray500,
    trackingEventBackground: Colors.anthrazit550,
    deviceBackground: Colors.plum1000,
    deviceText: Colors.mauve400,
    insightBackground: Colors.mauve900,
    insightText: Colors.mauve100,
    boxShadow: Colors.black,
    patientMessage: Colors.anthrazit600,
    careManagerMessage: Colors.anthrazit550,
    primaryButtonBackground: Colors.mauve900,
    primaryButtonText: Colors.white,
    primaryButtonHover: Colors.mauve800,
    secondaryButtonBackground: Colors.anthrazit550,
    secondaryButtonText: Colors.mauve700,
    secondaryButtonHover: Colors.anthrazit600,
  }

  const nativeFont = 'NotoSans'

  const font = {
    weight: {
      regular: {
        fontFamily: Platform.select({
          native: `${nativeFont}-Regular`,
          web: 'Noto Sans',
        }),
        fontWeight: 400,
      },
      medium: {
        fontFamily: Platform.select({
          native: `${nativeFont}-Medium`,
          web: 'Noto Sans',
        }),
        fontWeight: 500,
      },
      semiBold: {
        fontFamily: Platform.select({
          native: `${nativeFont}-SemiBold`,
          web: 'Noto Sans',
        }),
        fontWeight: 600,
      },
      bold: {
        fontFamily: Platform.select({
          native: `${nativeFont}-Bold`,
          web: 'Noto Sans',
        }),
        fontWeight: 700,
      },
      extraBold: {
        fontFamily: Platform.select({
          native: `${nativeFont}-ExtraBold`,
          web: 'Noto Sans',
        }),
        fontWeight: 800,
      },
      black: {
        fontFamily: Platform.select({
          native: `${nativeFont}-Black`,
          web: 'Noto Sans',
        }),
        fontWeight: 900,
      },
    },
    size: {
      tiny: 11,
      small: 12,
      medium: 16,
      large: 18,
      xLarge: 24,
      xxLarge: 32,
      button: 14,
      toast: 13,
      dialog: 14,
      settings: 14,
      toggle: 14,
      heading4: 20,
      heading2: 28,
    },
  }

  const borderRadius = {
    tiny: 3,
    small: 5,
    medium: 12,
    large: 20,
    xLarge: 28,
    switch: 8,
  }

  const maxWidth = 600

  return {
    height,
    width: width < maxWidth ? width : maxWidth,
    minWidth: 320,
    maxWidth,
    isLight,
    colors: isLight ? lightColors : darkColors,
    font,
    borderRadius,
  }
}
